import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import App from "./App.tsx";
import "./index.css";
import { reportWebVitals } from "./lib/webVitals";
import { recoverFromStaleChunk } from "./lib/chunkReload";

// Global stale-chunk recovery. Vite fires `vite:preloadError` when ANY
// dynamically-imported module fails to load — including nested vendor chunks
// (e.g. recharts/LineChart) that aren't route-level and so aren't wrapped by
// lazyRetry. After a deploy the old chunk hash 404s (or the SPA fallback
// returns index.html), so we recover by clearing the SW/caches and reloading.
window.addEventListener("vite:preloadError", (event) => {
  event.preventDefault(); // suppress the uncaught rejection; we handle it
  void recoverFromStaleChunk();
});

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
  });
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <App />
  </StrictMode>
);

// Fade the inline boot loader after React paints. Two rAFs ensures we wait
// past the first commit so we don't briefly flash an empty screen.
requestAnimationFrame(() => requestAnimationFrame(() => {
  const el = document.getElementById('initial-loader');
  if (el) {
    el.style.opacity = '0';
    setTimeout(() => el.remove(), 300);
  }
}));

reportWebVitals();
